import React, { type ReactNode } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { gitlabAuthApiRef, useApi, appThemeApiRef } from '@backstage/core-plugin-api';
import { appLanguageApiRef } from '@backstage/core-plugin-api/alpha';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { IconButton } from '@material-ui/core';
import { UnifiedThemeProvider } from '@backstage/theme';
import { myTheme } from './theme/theme';
import { GroupsPicker } from './components/catalog/GroupsPicker';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { translationResources } from './translations';
import { getStoredLanguage } from './utils/languageStorage';
import { getStoredTheme } from './utils/themeStorage';

const availableLanguages: ('pt-BR' | 'en')[] = ['pt-BR', 'en'];

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  __experimentalTranslations: {
    availableLanguages: availableLanguages,
    resources: translationResources,
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        providers={[
          {
            id: 'gitlab-auth-provider',
            title: 'GitLab',
            message: 'Sign in using GitLab',
            apiRef: gitlabAuthApiRef,
          }
        ]}
      />
    ),
  },
  themes: [
    {
      id: 'xvia-light-theme',
      title: 'X-Via Backstage Theme',
      variant: 'light',
      icon: <IconButton />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={myTheme}>{children}</UnifiedThemeProvider>
      ),
    },
  ],
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          filters={
            <GroupsPicker />
          }
        />
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
  </FlatRoutes>
);

const LanguageInitializer = ({ children }: { children: ReactNode }) => {
  const appLanguageApi = useApi(appLanguageApiRef);

  React.useLayoutEffect(() => {
    const storedLanguage = getStoredLanguage();
    const currentLanguage = appLanguageApi.getLanguage().language;

    if (currentLanguage !== storedLanguage) {
      appLanguageApi.setLanguage(storedLanguage);
    }
  }, [appLanguageApi]);

  return <>{children}</>;
};

const ThemeInitializer = ({ children }: { children: ReactNode }) => {
  const appThemeApi = useApi(appThemeApiRef);

  React.useLayoutEffect(() => {
    const storedTheme = getStoredTheme();

    if (storedTheme) {
      appThemeApi.setActiveThemeId(storedTheme);
    }
  }, [appThemeApi]);

  return <>{children}</>;
};

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <LanguageInitializer>
        <ThemeInitializer>
          <Root>{routes}</Root>
        </ThemeInitializer>
      </LanguageInitializer>
    </AppRouter>
  </>,
);
